import { ApolloError, useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { analyticsQueryContext } from './use-analytics-section';
import { GET_NOVA_ANALYTICS_SCOPES, type NovaAnalyticsDiscovery } from '../../../../queries/nova-analytics';

export function useAnalyticsDiscovery() {
  const client = useApolloClient();
  const [request, setRequest] = useState({ generation: 0, reportGeneration: 0, background: false });
  const pending = useRef(true);
  const lastRevalidation = useRef(-Infinity);
  const [response, setResponse] = useState<{
    generation: number;
    data?: NovaAnalyticsDiscovery;
    error?: ApolloError;
  }>();
  const refresh = useCallback(() => {
    pending.current = true;
    lastRevalidation.current = Date.now();
    setRequest(({ generation, reportGeneration }) => ({
      generation: generation + 1,
      reportGeneration: reportGeneration + 1,
      background: false,
    }));
  }, []);
  const revalidate = useCallback(() => {
    // A tab return commonly emits both visibilitychange and focus, sometimes after a fast response.
    if (pending.current || Date.now() - lastRevalidation.current < 1000) return;
    pending.current = true;
    lastRevalidation.current = Date.now();
    setRequest((current) => ({ ...current, generation: current.generation + 1, background: true }));
  }, []);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void client
      .query({
        query: GET_NOVA_ANALYTICS_SCOPES,
        fetchPolicy: 'no-cache',
        context: { ...analyticsQueryContext, fetchOptions: { signal: controller.signal } },
      })
      .then(({ data }) => {
        if (active) setResponse({ generation: request.generation, data: data.getNovaAnalyticsScopes ?? undefined });
      })
      .catch((cause: unknown) => {
        if (active)
          setResponse({
            generation: request.generation,
            error:
              cause instanceof ApolloError
                ? cause
                : new ApolloError({ networkError: cause instanceof Error ? cause : new Error('Discovery failed') }),
          });
      })
      .finally(() => {
        if (active) pending.current = false;
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [client, request.generation]);
  useEffect(() => {
    const visible = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [revalidate]);
  // Background checks retain the last authorized view only until the new result arrives.
  // Errors replace that result too, so a failed revalidation cannot retain the report.
  const current = response?.generation === request.generation || request.background ? response : undefined;
  return {
    ...current,
    generation: request.reportGeneration,
    loading: !current,
    refreshing: response?.generation !== request.generation,
    refresh,
  };
}
